import { useGetAccountsQuery } from "@/modules/account/services";
import type {
  IOwnAccountsTransferTemplateForm,
  IOwnAccountsTransferTemplateRequest
} from "@/modules/template/common/types";
import {
  useCreateOwnTransferTemplateMutation,
  useGetOwnTransferTemplateQuery,
  useUpdateOwnTransferTemplateMutation
} from "@/modules/template/services";
import { TransferKeys } from "@/modules/transaction/common/enum";
import { useOwnAccountTransfer } from "@/modules/transaction/hooks/useOwnAccountTransfer";
import { CURRENCY, INITIAL_PAGE_SIZE, INPUT_MAX_LIMIT, } from "@/shared/common/constants";
import { SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import { OwnAccountSelectOption, Select, TemplateLayout } from "@/shared/components";
import { getAmountOrNull, isValidAmount } from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import type { TItemValue } from "@ab.uitools/ui-library/types/globalTypes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  isEdit?: boolean;
  isCopy?: boolean;
}

export const OwnAccountsTransferTemplateForm = ({ isEdit }: Props) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const toAccount = searchParams.get(SearchParamKeys.ToAccount);
  const idempotencyKey = useRef(uuid4()).current;
  const templateName = searchParams.get(SearchParamKeys.TemplateName);

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const [createOwnTransferTemplateApi, {
    isLoading: isCreateTransferTemplateLoading
  }] = useCreateOwnTransferTemplateMutation();
  const [
    updateOwnTransferTemplateApi,
    { isLoading: isUpdateTransferTemplateLoading }
  ] = useUpdateOwnTransferTemplateMutation();

  const {
    data: accountsList = [],
    isLoading: isGetAccountsLoading,
  } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    reset,
    errors,
    setValue,
    values,
    creditAccountsFilteredList,
    debitAccountsFilteredList,
    handleClickDebitTarget,
    handleClickCreditTarget,
    handleSetCreditDebitAccountLists,
  } = useOwnAccountTransfer({ accountsList, isEdit });

  const {
    data: ownTransferTemplate,
    isLoading: isGetTemplateLoading,
  } = useGetOwnTransferTemplateQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: !isEdit && !operationId
  });

  const ownTransferTemplateAction = useCallback((values: IOwnAccountsTransferTemplateForm) => {
    const requestData: IOwnAccountsTransferTemplateRequest = {
      ...values,
      name: templateName ?? "",
      amount: values.amount || null,
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount),
      creditAccount: accountsList?.find(({ number }) => {
        return number === values.creditAccount;
      }),
    };

    return isEdit ?
      updateOwnTransferTemplateApi({
        data: requestData,
        id: operationId
      }).unwrap() :
      createOwnTransferTemplateApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList]);

  const setDefaultValues = () => {
    const data = {
      creditAccount: ownTransferTemplate?.creditAccount?.number,
      debitAccount: ownTransferTemplate?.debitAccount?.number,
      amount: getAmountOrNull(ownTransferTemplate?.amount),
      purpose: ownTransferTemplate?.purpose,
      currency: ownTransferTemplate?.debitAccount?.currency,
    };
    reset?.(data);
    setDefaultData(data);
    handleSetCreditDebitAccountLists(
      `${ownTransferTemplate?.creditAccount?.number}`,
      ownTransferTemplate?.debitAccount
    );
  };

  const currentData = useMemo(() => ({
    creditAccount: values.creditAccount,
    debitAccount: values.debitAccount,
    amount: getAmountOrNull(values.amount),
    purpose: values.purpose,
    currency: values.currency,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if (isEdit && !!ownTransferTemplate?.transactionType) {
      setDefaultValues();
    }
  }, [isEdit, ownTransferTemplate]);

  useEffect(() => {
    if (!isEdit && toAccount) {
      setValue(TransferKeys.CreditAccount, toAccount);
    }
  }, [toAccount]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.Purpose, t("transfer.ownAccounts.purpose"));
    }
  }, [isEdit]);

  return (
    <TemplateLayout
      title={t("template.ownAccounts.title")}
      page="Own account transfer template"
      isGetTemplateLoading={isGetTemplateLoading}
      isLoading={isCreateTransferTemplateLoading || isUpdateTransferTemplateLoading}
      isEdit={isEdit}
      withCommission={false}
      hasChanges={hasChanges}
      accountsList={accountsList}
      saveTemplate={ownTransferTemplateAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="debitAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    className="custom-select"
                    options={debitAccountsFilteredList}
                    isDisabled={isGetAccountsLoading}
                    label={t("transfer.debitAccount")}
                    placeholder={t("transfer.chooseDebitAccount")}
                    setSelectedValue={(value) => handleClickDebitTarget(value as TItemValue)}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="creditAccount"
                className="mb-8"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    className="custom-select"
                    options={creditAccountsFilteredList}
                    isDisabled={isGetAccountsLoading}
                    label={t("transfer.creditAccount")}
                    placeholder={t("transfer.chooseCreditAccount")}
                    setSelectedValue={(value) => handleClickCreditTarget(value as TItemValue)}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}/>
            </>,
            title: t("transfer.accountDetails"),
            value: "account_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="amount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    placeholder={t("transfer.enterAmount")}
                    thousandSeparator=","
                    decimalScale={values.currency === CURRENCY.AMD.label ? 1 : 2}
                    rightIconProps={{
                      Component: CURRENCY[values.currency]?.symbolIcon || "",
                    }}
                    label={t("general.amount")}
                    isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                  />
                )}/>
              <FormField
                name="purpose"
                className="mb-8"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
            </>,
            title: t("transfer.transferDetails"),
            value: "transfer_details",
            isOpen: true,
          }
        ]}/>
    </TemplateLayout>
  );
};
